// The Pipeline board (Iris's screen 1): where does every open opportunity stand? The header band says each stage's count
// and value (solid for priced, hatched for typed, against the biggest stage); a column of cards under each; Won inside its
// window; Parked and Lost to the side. A lead that needs Spencer is highlighted where it sits: no strip, no rank
// (Spencer, 2026-10-19). Click a stage for its steps.

import { Link, useNavigate } from 'react-router-dom';
import type { PipelineStage } from '@zerogap/engine';
import { money, monthDay } from '../../lib/format';
import { seatWords, type Lead, type Pipeline } from '../../lib/pipeline';
import { useSession } from '../../lib/session';
import { Empty, Loading, onLink, pressable, Tag } from '../../ui/kit';
import { LeadValue, Likelihood, NextTouch, ParkedRow, SeatTag, Strip, UndoRow, useTaps } from './bits';

export function stageOf(p: Pipeline, lead: Lead): PipelineStage | null {
  return lead.stage;
}

/** A card on the board. */
export function LeadCard({ p, lead }: { p: Pipeline; lead: Lead }) {
  const nav = useNavigate();
  const won = lead.outcome === 'won';
  return (
    <div className={`pcard${lead.needsOwner || (lead.outcome === 'open' && (lead.late || lead.quiet)) ? ' needs' : ''}${lead.placed || won ? '' : ' unplaced'}`} {...pressable((e) => !onLink(e) && nav(`/opportunities/${lead.id}`))}>
      <Link to={`/opportunities/${lead.id}`} className="pname rowlink">
        {lead.name}
      </Link>
      <div className="prow">
        <LeadValue lead={lead} />
        {won ? <small>Won {monthDay(lead.opp.outcome_at)}</small> : <Likelihood lead={lead} />}
      </div>
      {won ? (
        <small>
          {lead.opp.anchor_type ? `${lead.opp.anchor_type} ${monthDay(lead.opp.anchor_date)}` : 'No anchor'} · {lead.step ? lead.step.name : 'no open steps'}
        </small>
      ) : (
        <>
          <Strip lead={lead} />
          <div className="pnext">
            {lead.step ? (
              <>
                {lead.step.name}
                {lead.readyRow && <small> · ready</small>} <SeatTag p={p} step={lead.step} />
              </>
            ) : (
              <Link to="/pipeline/place" className="plink" onClick={(e) => e.stopPropagation()}>
                Not placed yet · place it
              </Link>
            )}
          </div>
          <div>
            <NextTouch lead={lead} />
            {lead.replyBy && lead.step && !seatWords(p, lead.step).you && <small> · reply-by {lead.replyLate ? `${monthDay(lead.replyBy)} · ${lead.replyLate} d late` : monthDay(lead.replyBy)}</small>}
          </div>
        </>
      )}
    </div>
  );
}

/** The header band's value bar: priced solid, typed hatched, against the biggest stage. */
function ValueBar({ priced, typed, most, dark }: { priced: number; typed: number; most: number; dark?: boolean }) {
  if (most <= 0) return null;
  const w = (n: number) => `${Math.max(0, Math.min(100, (n / most) * 100))}%`;
  return (
    <span className={`pvbar${dark ? ' dark' : ''}`}>
      <i className="solid" style={{ width: w(priced) }} />
      <i className="hatch" style={{ width: w(typed) }} />
    </span>
  );
}

export function stageTotals(leads: Lead[]) {
  let priced = 0;
  let typed = 0;
  let unvalued = 0;
  for (const l of leads) {
    if (l.value.kind === 'priced') priced += l.value.cents ?? 0;
    else if (l.value.kind === 'typed' || l.value.kind === 'range') typed += l.value.cents ?? 0;
    else unvalued++;
  }
  return { priced, typed, total: priced + typed, unvalued };
}

export function Board({ p, inHome }: { p: Pipeline; inHome?: boolean }) {
  const { can, device } = useSession();
  const taps = useTaps();
  if (p.loading) return <Loading what="Loading the pipeline" />;
  if (p.leads.length === 0) return <Empty>No opportunities yet. Use + New in the top bar.</Empty>;
  const inStage = (st: PipelineStage) => p.open.filter((l) => l.stage?.id === st.id);
  const columns = p.working.map((st) => ({ stage: st, leads: inStage(st), totals: stageTotals(inStage(st)) }));
  const wonTotals = stageTotals(p.wonRecent);
  const most = Math.max(...columns.map((c) => c.totals.total), wonTotals.total, 1);
  const unplaced = p.open.filter((l) => !l.placed).length + p.parked.filter((l) => !l.placed).length;
  const parkedTotal = stageTotals(p.parked).total;
  const lostTotal = stageTotals(p.lost).total;
  const prices = can('see_prices');

  // the phone: the band as a list, each stage one tap away (the cards live on the stage screen there)
  if (device === 'phone') {
    return (
      <div className="stack">
        {unplaced > 0 && (
          <Link to="/pipeline/place" className="tile soft">
            <b>{unplaced} leads not placed on a step yet</b>
            <br />
            <small>Place each one: its step, first visit, design lane, anchor, next touch.</small>
          </Link>
        )}
        <div className="pband list">
          {columns.map((c) => (
            <Link key={c.stage.id} to={`/pipeline/stage/${c.stage.key}`} className="pbc">
              <span className="psn">
                <span>{c.stage.name}</span>
                <span className="num">{c.leads.length}</span>
              </span>
              <span className="psv num">{prices ? (c.totals.total ? `${c.totals.priced ? '' : '~'}${money(c.totals.total, { whole: true })}` : '—') : ''}</span>
              {prices && <ValueBar priced={c.totals.priced} typed={c.totals.typed} most={most} dark />}
            </Link>
          ))}
          <Link to="/pipeline/stage/won" className="pbc">
            <span className="psn">
              <span>Won <Tag kind="off">{p.wonWindowDays} d</Tag></span>
              <span className="num">{p.wonRecent.length}</span>
            </span>
            <span className="psv num">{prices ? money(wonTotals.total, { whole: true }) : ''}</span>
          </Link>
          <Link to="/pipeline/stage/parked" className="pbc side">
            <span className="psn">
              <span>Parked</span>
              <span className="num">{p.parked.length}{prices ? ` · ${money(parkedTotal, { whole: true })}` : ''}</span>
            </span>
          </Link>
          <Link to="/pipeline/won-lost" className="pbc side">
            <span className="psn">
              <span>Lost</span>
              <span className="num">{p.lost.length}{prices ? ` · ${money(lostTotal, { whole: true })}` : ''}</span>
            </span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      {unplaced > 0 && (
        <Link to="/pipeline/place" className="tile soft" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <span>
            <b>{unplaced} open or parked leads are not placed on a step yet.</b> Place each one once: its step, first visit, design lane, anchor, next touch.
          </span>
          <span className="btn p">Placement page</span>
        </Link>
      )}
      <div className={`pboard${inHome ? ' home' : ''}`}>
        <div className="pband">
          {columns.map((c) => (
            <Link key={c.stage.id} to={`/pipeline/stage/${c.stage.key}`} className="pbc">
              <span className="psn">
                <span>{c.stage.name}</span>
                <span className="num">{c.leads.length}</span>
              </span>
              <span className="psv num">{prices ? (c.totals.total ? `${c.totals.priced ? '' : '~'}${money(c.totals.total, { whole: true })}` : 'No value yet') : ''}</span>
              {prices && <ValueBar priced={c.totals.priced} typed={c.totals.typed} most={most} dark />}
              <small>
                {prices && c.totals.priced > 0 && c.totals.typed > 0 ? `${money(c.totals.priced, { whole: true })} priced · ${money(c.totals.typed, { whole: true })} typed` : prices && c.totals.priced > 0 ? `${money(c.totals.priced, { whole: true })} priced` : ''}
                {c.totals.unvalued > 0 ? `${prices && c.totals.total ? ' · ' : ''}${c.totals.unvalued} not valued` : ''}
              </small>
            </Link>
          ))}
          <Link to="/pipeline/stage/won" className="pbc">
            <span className="psn">
              <span>
                Won <Tag kind="off" title="The Won window is the won_window_days setting">{p.wonWindowDays} d</Tag>
              </span>
              <span className="num">{p.wonRecent.length}</span>
            </span>
            <span className="psv num">{prices ? money(wonTotals.total, { whole: true }) : ''}</span>
            <small>
              Last {p.wonWindowDays} days · {p.wonRecent.filter((l) => l.step).length ? `${p.wonRecent.filter((l) => l.step).length} with open steps` : 'no open steps'}
            </small>
          </Link>
        </div>
        {columns.map((c) => (
          <div key={c.stage.id} className="pcol">
            {c.leads.length === 0 && <div className="pempty">Nothing at {c.stage.name}</div>}
            {c.leads.map((l) => (
              <LeadCard key={l.id} p={p} lead={l} />
            ))}
          </div>
        ))}
        <div className="pcol">
          {p.wonRecent.map((l) => (
            <LeadCard key={l.id} p={p} lead={l} />
          ))}
          <div className="pempty">
            Last {p.wonWindowDays} days · {p.wonRecent.length}{prices ? ` · ${money(wonTotals.total, { whole: true })}` : ''}
            <br />
            <Link to="/pipeline/won-lost" className="plink">
              Won and lost · all time
            </Link>
          </div>
        </div>
        <div className="pside">
          <div className="psidebox">
            <div className="psn">
              <span>Parked</span>
              <span className="num">
                {p.parked.length}
                {prices ? ` · ${money(parkedTotal, { whole: true })}` : ''}
              </span>
            </div>
            <div className="lb" style={{ margin: '6px 0 2px' }}>
              By re-approach date
            </div>
            {p.parked.length === 0 && <small>Nothing parked.</small>}
            {p.parked.map((l) => {
              const tap = taps.tap(l.id);
              return tap ? <UndoRow key={l.id} tap={tap} onUndo={() => void taps.undo(l.id)} /> : <ParkedRow key={l.id} lead={l} onTap={(t) => taps.remember(l.id, t)} />;
            })}
          </div>
          <div className="psidebox">
            <div className="psn">
              <span>Lost</span>
              <span className="num">
                {p.lost.length}
                {prices ? ` · ${money(lostTotal, { whole: true })}` : ''}
              </span>
            </div>
            <div style={{ marginTop: 6 }}>
              <Link to="/pipeline/won-lost" className="plink">
                Won and lost
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
